import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Forces every navigation that lands on a new route to start at the top of the page.
 *
 * Why this exists: React Router (history API based) does NOT reset window.scrollY between
 * route transitions. Combined with the browser's own scrollRestoration='auto', that means
 * tapping series A (scrolling down to read its details), tapping Back, then tapping series
 * B can leave the user looking at the BOTTOM of series B's page — because the new page
 * inherits A's scroll position before its content has settled.
 *
 * Behaviour:
 *   • PUSH navigations (e.g. tapping a series tile)         → scroll to (0, 0)
 *   • REPLACE navigations (programmatic redirects)          → scroll to (0, 0)
 *   • POP navigations (browser Back / Forward)              → leave scroll alone so the
 *     browser's native behaviour (or per-page restore logic, e.g. CatalogPage) wins.
 *
 * The `/catalog` exception lets CatalogPage do its own sessionStorage-backed scroll
 * restoration even when the user opens it as a fresh PUSH (e.g. tapping the Catalog nav).
 * If they explicitly clicked the nav link they're usually fine landing wherever they were
 * last; if they had no prior scroll it stays at 0.
 */
export default function ScrollManager() {
  const { pathname } = useLocation();
  const navType = useNavigationType(); // "PUSH" | "REPLACE" | "POP"
  const prevPath = useRef(pathname);

  useEffect(() => {
    // Opt out of browser-managed scroll restoration so our logic is the only thing
    // touching scroll position. Otherwise Chrome/Safari race us with their own restore.
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  useEffect(() => {
    const isSame = prevPath.current === pathname;
    prevPath.current = pathname;
    if (isSame) return; // no navigation happened, just a re-render
    if (navType === "POP") return; // back/forward → CatalogPage or browser handles it
    // These pages run their own sessionStorage-backed scroll restore on mount, so we
    // must NOT force them to (0, 0) — that would defeat the whole point.
    if (pathname.startsWith("/catalog")) return;
    if (pathname.startsWith("/admin/cards")) return;
    // Run after the new route's components have mounted/painted so we override any
    // residual scroll position from the outgoing page.
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }, [pathname, navType]);

  return null;
}
